import React, { useState } from 'react';
import { Button, Modal, Spinner } from "@tag/tag-components-react-v4";
import { CustomerDTO } from "../types/CustomerDTO";
import LockSiteService from "../services/LockSiteService";
import { ProjectAccents } from "../../General/config/projectAccents";
import PipelineStatusDisplay from "../../General/components/PipelineStatusDisplay";

interface LockSiteResult {
    buildUrl?: string;
    buildId?: number;
    pipelineId?: number;
}

function LockSiteModal(props: Readonly<{
    visible: boolean,
    selectedCustomer: CustomerDTO,
    handleClose: () => void,
    handleCustomerMenuModalClose: Function,
    setAlert: Function
}>) {

    const [loading, setLoading] = useState(false);
    const [pipelineResult, setPipelineResult] = useState<LockSiteResult>();
    const [reason, setReason] = useState<string>('');

    const handleConfirmLock = async () => {
        setLoading(true);

        try {
            const result = await LockSiteService.lockSite(props.selectedCustomer.accGroup, props.selectedCustomer.clientId, reason || undefined);

            if (result.success) {
                setPipelineResult({
                    buildUrl: result.buildUrl,
                    buildId: result.buildId,
                    pipelineId: result.pipelineId
                });
            } else {
                props.setAlert({ type: 'error', message: result.errorMessage || 'Failed to trigger lock site pipeline' });
                props.handleClose();
            }
        } catch (e: any) {
            props.setAlert({ type: 'error', message: e.message || 'An error occurred, please contact HSC DevOps' });
            props.handleClose();
        } finally {
            setLoading(false);
        }
    };

    const renderModalContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '1em', padding: '1em 0' }}>
                    <Spinner />
                    <span>Triggering lock site pipeline...</span>
                </div>
            );
        }

        if (pipelineResult) {
            return (
                <>
                    <p>Pipeline triggered successfully for <strong>{props.selectedCustomer.name}</strong>!</p>
                    {pipelineResult.pipelineId && pipelineResult.buildId ? (
                        <PipelineStatusDisplay
                            apiBaseUrl={process.env.REACT_APP_SELF_SERVICE_CARECOMPLIANCE_API_URL}
                            pipelineId={pipelineResult.pipelineId}
                            runId={pipelineResult.buildId}
                            buildUrl={pipelineResult.buildUrl}
                        />
                    ) : pipelineResult.buildUrl ? (
                        <p>
                            <a href={pipelineResult.buildUrl} target="_blank" rel="noopener noreferrer">
                                View Pipeline Run
                            </a>
                        </p>
                    ) : pipelineResult.buildId ? (
                        <p>Pipeline Run ID: <strong>{pipelineResult.buildId}</strong></p>
                    ) : null}
                </>
            );
        }

        return (
            <>
                <p>Are you sure you want to lock site <strong>{props.selectedCustomer.name}</strong> ({props.selectedCustomer.urlName}) for termination?</p>
                <div style={{ marginTop: '1em' }}>
                    <label htmlFor="lock-reason" style={{ display: 'block', marginBottom: '0.5em', fontWeight: 'bold' }}>Reason:</label>
                    <textarea
                        id="lock-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for locking this site..."
                        style={{ width: '100%', minHeight: '80px', padding: '0.5em', resize: 'vertical' }}
                    />
                </div>
                <div style={{ display: 'flex', gap: '1em', marginTop: '1em' }}>
                    <Button text={'Confirm'} onClick={handleConfirmLock} />
                </div>
            </>
        );
    };

    return (
        <Modal
            visible={props.visible}
            accent={ProjectAccents.CareCompliance}
            heading={pipelineResult ? 'Lock Site Triggered' : 'Lock Site for Termination'}
            onCancelButtonClick={() => props.handleClose()}
            onClose={() => props.handleClose()}
            primaryButtonProps={{ buttonStyle: { 'display': 'none' } }}
        >
            <div>
                {renderModalContent()}
            </div>
        </Modal>
    );
}

export default LockSiteModal;
